import { createSlice } from '@reduxjs/toolkit'
import { getFromLocalStorage, setToLocalStorage } from '../utils/localStorage'
import { toCamelCaseString } from '../utils/functions'

const initialState = {
  showLoader: 0,
  loaderMessage: '',
  snackbar: {},
  appConfigDetails: {},
  transactionalData: {},
  translatedData: require('../utils/defaultTranslatedData.json'),
  languageCode: getFromLocalStorage('languageCode') || { code: 'en', text: 'English' },
  showWhiteScreen: false,
  errorMessage: { name: '', message: '' },
  addressConfig: [],
  stateList: [],
  userLocations: [],
  countryAddressConfig: [],
  selectedPlanSalesFlow: 'store',
  notifications: [],
  showChangeLanguageOption: false,
  showMobileFooter: false,
}

const commonSlice = createSlice({
  name: 'commonSlice',
  initialState,
  reducers: {
    setLoader: (state, action) => {
      const showLoader = action.payload?.showLoader || action.payload
      state.showLoader = showLoader ? state.showLoader + 1 : state.showLoader - 1
      state.loaderMessage = action.payload?.loaderMessage
    },
    setSnackbar: (state, action) => {
      state.snackbar = action.payload
    },
    setTranslatedData: (state, action) => {
      let defaultTranslatedText = require('../utils/defaultTranslatedData.json')
      let payloadData = action.payload
      let keys = Object.keys(defaultTranslatedText)

      keys.forEach((key) => {
        if (typeof defaultTranslatedText[key] === 'object') {
          Object.keys(defaultTranslatedText[key]).forEach((item) => {
            if ([undefined, ' '].indexOf(payloadData[key]) !== -1) {
              payloadData[key] = defaultTranslatedText[key]
            } else if ([undefined, ' '].indexOf(payloadData[key][item]) !== -1) {
              payloadData[key][item] = defaultTranslatedText[key][item]
            }
          })
        }
      })

      state.translatedData = payloadData
    },
    setLanguageCode: (state, action) => {
      state.languageCode = { code: action.payload.code, text: action.payload.text }
    },
    setAppConfigDetails: (state, action) => {
      state.appConfigDetails = {
        ...action.payload,
        consents: action.payload.consents?.reduce((newConsents, value) => {
          // Generic handling of consents.
          return {
            ...newConsents,
            [value.consentName]: {
              ...value,
              label: value.consentDisplayText,
              link: value.consentValue,
            },
          }
        }, {}),
        productImageUrl:
          action.payload.S3baseUrl +
          action.payload.productSubCategorybucket +
          '/subcategory/' +
          (process.env.NODE_ENV === 'dev' ? 'staging/' : ''),
      }
    },
    setTransactionalData: (state, action) => {
      setToLocalStorage('transactionalData', { ...getFromLocalStorage('transactionalData', {}), ...action.payload })
      state.transactionalData = { ...state.transactionalData, ...action.payload }
    },
    clearTransactionalData: (state, action) => {
      state.transactionalData = action.payload
    },
    setSelectedCountryDetails: (state, action) => {
      window.countryCode = action.payload.countryCode
      window.countryID = action.payload.countryID
      window.languageCode = action.payload.countryConfig?.languageCode?.DEFAULT

      state.selectedCountryDetails = action.payload
    },
    setShowWhiteScreen: (state, action) => {
      state.showWhiteScreen = action.payload
    },
    setErrorMessage: (state, action) => {
      state.errorMessage = action.payload
    },
    setAddressConfig: (state, action) => {
      const addressConfig = action.payload.countries[0].countryConfig.addressConfig
      const sortedAddressConfig = addressConfig.sort((a, b) => a.displayOrder - b.displayOrder)
      if (sortedAddressConfig[0]['keyName'] === 'UserLandmark') {
        let x = sortedAddressConfig[0]
        sortedAddressConfig[0] = sortedAddressConfig[1]
        sortedAddressConfig[1] = x
      }
      state.addressConfig = sortedAddressConfig?.reduce((a, v) => ({ ...a, [toCamelCaseString(v.keyName)]: v }), [])
    },
    setStateList: (state, action) => {
      state.stateList = action.payload.map((state) => ({ ...state, value: state.state }))
    },
    setUserLocations: (state, action) => {
      state.userLocations = action.payload
    },
    setCountryAddressConfig: (state, action) => {
      state.countryAddressConfig = action.payload
    },
    // Below action decides whether store/subscription flow
    setSelectedPlanSalesFlow: (state, action) => {
      state.selectedPlanSalesFlow = action.payload
    },

    setNotifications: (state, action) => {
      state.notifications = action.payload
    },
    setShowChangeLanguageOption: (state, action) => {
      state.showChangeLanguageOption = action.payload
    },
    setShowMobileFooter: (state, action) => {
      state.showMobileFooter = action.payload
    },
  },
})

export const {
  setLoader,
  setSnackbar,
  setTranslatedData,
  setLanguageCode,
  setAppConfigDetails,
  setTransactionalData,
  clearTransactionalData,
  setSelectedCountryDetails,
  setShowWhiteScreen,
  setErrorMessage,
  setAddressConfig,
  setStateList,
  setUserLocations,
  setNotifications,
  setCountryAddressConfig,
  setSelectedPlanSalesFlow,
  setShowChangeLanguageOption,
  setShowMobileFooter,
} = commonSlice.actions

export default commonSlice.reducer
